import createProxyFromRef from '@lib/utils/proxy/createProxyFromRef';
import { FormikConfig, FormikErrors, FormikProps } from 'formik';
import React from 'react';

/**
 * Return if we should show error
 * @author boris.nanista
 * @param errors errors from formik
 * @param touched touched flags from formik
 * @returns error object
 */
export function buildErrorsVisibility<T>(errors: FormikErrors<T>, touched: any) {
  return Object.keys(errors).reduce((acc, key) => {
    acc[key as keyof T] = Boolean(errors[key as keyof T] && touched[key as keyof T]);

    return acc;
  }, {} as Record<keyof T, boolean>);
}

type TFormReturn<ValuesT> = [FormikProps<ValuesT> | null, FormikConfig<ValuesT>];

/**
 * Hook to get instance of form in parent component,
 * and to cleaner configuration
 * @param params params for form
 * @returns [ref to form, props for form component]
 */
export function useFormHook<ValuesT extends Record<string, any>>(params: FormikConfig<ValuesT>) {
  const valuesRef = React.useRef<FormikProps<ValuesT> | null>(null);

  /**
   * Use proxy element to get rid off `.current`
   */
  const proxyForm = React.useRef(createProxyFromRef(valuesRef));

  return [
    proxyForm.current,
    {
      ...params,
      innerRef: (r: FormikProps<ValuesT>) => {
        valuesRef.current = r;
      },
    },
  ] as TFormReturn<ValuesT>;
}
